import React, { useState } from "react";
import { Button, Card, Col, Stack } from "react-bootstrap";
import Input from "./Input";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faClose, faEdit, faGripLines, faTrash } from "@fortawesome/free-solid-svg-icons";
import IconButton from "./IconButton";
import { Tooltip } from "react-tooltip";

interface CardState {
  items: any[];
  onDeleteFinal: any;
  updateOrder: any;
  onEditFinal?: any;
  customRenderEdit?: any;
  customEditId?: string;
  handleEdit?: any;
  setErrMessage: any;
}

const ReorderEditDeleteCards = ({
  items,
  onDeleteFinal,
  updateOrder,
  onEditFinal,
  customRenderEdit,
  customEditId,
  handleEdit,
  setErrMessage,
}: CardState) => {
  const [editId, setEditId] = useState<string>();
  const [deleteId, setDeleteId] = useState<string>();
  const [updatedItemName, setUpdatedItemName] = useState<string>();
  const [updatedItemType, setUpdatedItemType] = useState<string | null>(null);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dropTargetIndex, setDropTargetIndex] = useState<number | null>(null);
  const [draggedProgramId, setDraggedProgramId] = useState<number | null>(null);

  const onClickEdit = (itemId: string) => {
    const itemName = items.find((i: any) => i.id === itemId).name;
    const itemType = items.find((i: any) => i.id === itemId).type;
    setUpdatedItemName(itemName);
    setUpdatedItemType(itemType);

    if (customRenderEdit) {
      handleEdit(itemId);
    } else {
      setEditId(itemId);
    }
    setDeleteId("");
  };

  const onClickEditFinal = (itemId: string) => {
    if (!updatedItemName || updatedItemName.trim() === "") {
      setErrMessage("Please enter a value.");
      return;
    }

    setEditId("");
    onEditFinal(itemId, updatedItemName);
    setUpdatedItemName("");
  };

  const onClickCancelEdit = () => {
    setEditId("");
    setUpdatedItemName("");
  };

  const onClickDelete = (itemId: string) => {
    const item = items.find((i: any) => i.id === itemId);
    setUpdatedItemName(item?.name);
    setEditId("");
    setDeleteId(itemId);
  };

  const onClickDeleteFinal = (itemId: string) => {
    setDeleteId("");
    onDeleteFinal(itemId, updatedItemName);
    setUpdatedItemName("");
  };

  const onClickDeleteCancel = () => {
    setDeleteId("");
    setUpdatedItemName("");
  };

  const updateItem = (e: any) => {
    setUpdatedItemName(e.target.value);
  };

  const handleDrop = (dropIndex: number) => {
    if (draggedIndex === null || draggedIndex === dropIndex) return;

    const reordered = [...items];
    const [moved] = reordered.splice(draggedIndex, 1);
    reordered.splice(dropIndex, 0, moved);
    updateOrder(reordered);
    setDraggedIndex(null);
  };

  const renderDelete = (itemId: any) => {
    return (
      <Card>
        <Card.Body>
          <Stack direction="horizontal" gap={3}>
            <Stack style={{ justifyContent: "center" }} gap={2}>
              <Card.Subtitle>Remove {updatedItemName}?</Card.Subtitle>
            </Stack>
            <Button className="app-danger-color button" onClick={() => onClickDeleteFinal(itemId)}>
              Confirm Delete
            </Button>
            <div className="vr" />
            <Button className="app-primary-color button" onClick={() => onClickDeleteCancel()}>
              Cancel
            </Button>
          </Stack>
        </Card.Body>
      </Card>
    );
  };

  const renderEdit = (itemId: any) => {
    return (
      <Card>
        <Card.Body>
          <Stack direction="horizontal" gap={3}>
            <Col>
              <Input label="Name" type="text" value={updatedItemName} hasFocus={true} onUpdate={(e: any) => updateItem(e)} />
            </Col>
            {updatedItemType && (
              <Col>
                <Input label="Type" type="text" value={updatedItemType} hasFocus={true} onUpdate={(e: any) => setUpdatedItemType(e)} />
              </Col>
            )}
            <IconButton onClickButton={() => onClickEditFinal(itemId)} tooltipText={"Save"} icon={faCheck} />
            <div className="vr" />
            <IconButton onClickButton={() => onClickCancelEdit()} tooltipText={"Cancel"} icon={faClose} />
          </Stack>
        </Card.Body>
      </Card>
    );
  };

  return (
    <>
      {items?.map((item: any, index: number) =>
        deleteId === item.id ? (
          <div key={"render-delete-" + item.id}>{renderDelete(item.id)}</div>
        ) : customEditId === item.id ? (
          <div key={"render-edit-" + item.id}>{customRenderEdit(item.id)}</div>
        ) : editId === item.id ? (
          <div key={"render-edit-" + item.id}>{renderEdit(item.id)}</div>
        ) : (
          <div
            key={item.id}
            draggable
            onDragStart={(e) => {
              setDraggedIndex(index);
              setDraggedProgramId(item.id);
              e.currentTarget.classList.add("dragging");
            }}
            onDragOver={(e) => {
              e.preventDefault();
              if (draggedProgramId === item.id) {
                setDropTargetIndex(null);
              } else {
                setDropTargetIndex(index);
              }
            }}
            onDragLeave={() => setDropTargetIndex(null)}
            onDrop={() => {
              if (draggedProgramId !== item.id) {
                handleDrop(index);
              }
              setDropTargetIndex(null);
              setDraggedProgramId(null);
            }}
            onDragEnd={() => {
              document.querySelectorAll(".dragging").forEach((el) => el.classList.remove("dragging"));
              setDraggedIndex(null);
              setDraggedProgramId(null);
              setDropTargetIndex(null);
            }}
            className={dropTargetIndex === index ? "drop-target" : ""}
          >
            <Card>
              <Card.Body>
                <Stack direction="horizontal" gap={3}>
                  <Col style={{ width: "20.5rem", overflow: "hidden", textOverflow: "ellipsis" }}>
                    <span style={{ fontSize: "1rem", cursor: "grab" }} data-tooltip-id="tooltip" data-tooltip-content={"Drag to Reorder"}>
                      <FontAwesomeIcon icon={faGripLines} className={"me-2"} />
                    </span>
                    <Tooltip id="tooltip" place="top" style={{ padding: "5px 10px" }} />
                    <small>{item.name}</small>
                  </Col>
                  {item?.type && (
                    <>
                      <small>{item.type}</small>
                      <div className="vr" />
                    </>
                  )}
                  <IconButton onClickButton={() => onClickEdit(item.id)} tooltipText={"Edit"} icon={faEdit} />
                  <div className="vr" />
                  <IconButton onClickButton={() => onClickDelete(item.id)} tooltipText={"Delete"} icon={faTrash} />
                </Stack>
              </Card.Body>
            </Card>
          </div>
        ),
      )}
    </>
  );
};

export default ReorderEditDeleteCards;
